"use client";

import React from "react";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CustomerService } from "@/services/CustomerService";
import type { CustomersOutput } from "@/interfaces/customers";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import {
  Trash,
  Plus,
  Filter,
  X,
  Edit,
  User,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import type { TagsOutput, PaginatedResponse, Pagination } from "@/interfaces";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useDeleteConfirmation } from "@/hooks/use-delete-confirmation";
import { DeleteConfirmationAlert } from "@/components/DeleteConfirmationAlert";
import { cn } from "@/lib/utils";
import {
  formatDddPhone,
  formatPhoneNumber,
  formatWhatsappLink,
} from "@/util/formatters";

type FilterType = "query" | "tags";

interface FilterItem {
  type: FilterType;
  value: string | string[];
}

const FilterContent = ({
  addFilter,
  availableTags,
  onClose,
}: {
  addFilter: (type: FilterType, value: string | string[]) => void;
  availableTags: TagsOutput[];
  onClose?: () => void;
}) => (
  <div className="space-y-4">
    <div>
      <h4 className="font-medium mb-2">Nome</h4>
      <div className="flex space-x-2">
        <Input
          placeholder="Digite o nome"
          className="h-8"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const value = (e.target as HTMLInputElement).value;
              if (value) {
                addFilter("query", value);
                onClose?.();
                (e.target as HTMLInputElement).value = "";
              }
            }
          }}
        />
      </div>
    </div>
    <div>
      <h4 className="font-medium mb-2">Tags</h4>
      <Select
        onValueChange={(value) => {
          if (value) {
            addFilter("tags", [value]);
            onClose?.();
          }
        }}
      >
        <SelectTrigger className="h-8">
          <SelectValue placeholder="Selecione as tags" />
        </SelectTrigger>
        <SelectContent>
          {availableTags.map((tag) => (
            <SelectItem key={tag.id} value={tag.id}>
              {tag.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  </div>
);

const CustomerCard = ({
  customer,
  onEdit,
  onDelete,
}: {
  customer: CustomersOutput;
  onEdit: () => void;
  onDelete: () => void;
}) => (
  <div className="p-4 border-b last:border-b-0 space-y-2">
    <div className="flex justify-between items-start">
      <div>
        <h3 className="font-medium">{customer.name}</h3>
        <p className="text-sm text-gray-500">
          {formatDddPhone(
            customer.ddd,
            customer.phoneNumber,
            customer.internationalCode
          )}
        </p>
      </div>
      <div className="flex gap-2">
        {formatWhatsappLink(
          customer.ddd,
          customer.phoneNumber,
          customer.internationalCode
        ) && (
          <Button
            variant="outline"
            size="sm"
            className="border-green-500 text-green-500 hover:bg-green-50"
            onClick={() =>
              window.open(
                formatWhatsappLink(
                  customer.ddd,
                  customer.phoneNumber,
                  customer.internationalCode
                )!,
                "_blank"
              )
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-4 w-4 mr-1"
            >
              <path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21" />
              <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1Z" />
              <path d="M13.5 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1Z" />
              <path d="M9 13.5a.5.5 0 0 0 .5.5h5a.5.5 0 0 0 0-1h-5a.5.5 0 0 0-.5.5Z" />
            </svg>
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          className="border-primary text-primary hover:bg-primary/10"
          onClick={onEdit}
        >
          <Edit className="h-4 w-4 mr-1" />
          Editar
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={onDelete}
        >
          <Trash className="h-4 w-4" />
        </Button>
      </div>
    </div>
    {(customer.tags as TagsOutput[]).length > 0 && (
      <div className="flex flex-wrap gap-1">
        {(customer.tags as TagsOutput[]).map((tag) => (
          <Badge
            key={tag.id}
            style={{
              backgroundColor: tag.styling?.primaryColor ?? "#E0E0E0",
              color: tag.styling?.fontColor ?? "#000000",
              border: `1px solid ${tag.styling?.secondaryColor ?? "#BDBDBD"}`,
            }}
            className="rounded-sm px-1 py-0.5 text-xs"
          >
            {tag.name}
          </Badge>
        ))}
      </div>
    )}
  </div>
);

const PaginationControls = ({
  pagination,
  onPageChange,
  onPageSizeChange,
}: {
  pagination: Pagination;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}) => {
  const { page, pageSize, total, pages } = pagination;
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 px-2">
      <div className="text-sm text-gray-500">
        Mostrando {total > 0 ? startItem : 0} a {endItem} de {total} clientes
      </div>

      <div className="flex items-center gap-2">
        <div className="flex items-center mr-4">
          <span className="text-sm text-gray-500 mr-2">Itens por página:</span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={pageSize.toString()} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5</SelectItem>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(pages, 5) }, (_, i) => {
              let pageNumber: number;

              // Logic to show pages around current page
              if (pages <= 5) {
                pageNumber = i + 1;
              } else if (page <= 3) {
                pageNumber = i + 1;
              } else if (page >= pages - 2) {
                pageNumber = pages - 4 + i;
              } else {
                pageNumber = page - 2 + i;
              }

              return (
                <Button
                  key={pageNumber}
                  variant={page === pageNumber ? "default" : "outline"}
                  size="icon"
                  className={cn(
                    "h-8 w-8",
                    page === pageNumber && "bg-[#EE7B11] hover:bg-[#d06a0a]"
                  )}
                  onClick={() => onPageChange(pageNumber)}
                >
                  {pageNumber}
                </Button>
              );
            })}
          </div>

          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const Customers = () => {
  const [customers, setCustomers] = useState<CustomersOutput[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<FilterItem[]>([]);
  const [availableTags, setAvailableTags] = useState<TagsOutput[]>([]);
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  // Remover o estado searchParams
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: 10,
    total: 0,
    pages: 0,
    links: {
      self: "",
    },
  });

  const navigate = useNavigate();
  const service = new CustomerService();

  // Referência para controlar chamadas duplicadas
  const isFetchingRef = React.useRef(false);

  const {
    isOpen,
    itemToDelete,
    open: openDeleteDialog,
    close: closeDeleteDialog,
  } = useDeleteConfirmation<string>();

  // Remover os useEffect que lidam com URL params

  const fetchCustomers = async () => {
    // Evitar chamadas duplicadas
    if (isFetchingRef.current) return;

    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      const queryParams: Record<string, string | string[] | number> = {
        expand: "tags",
        page: pagination.page,
        pageSize: pagination.pageSize,
      };

      filters.forEach((filter) => {
        if (filter.type === "tags") {
          queryParams.tags = filter.value as string[];
        } else {
          queryParams[filter.type] = filter.value as string;
        }
      });

      const response: PaginatedResponse<CustomersOutput> =
        await service.findAll(queryParams);

      setCustomers(response.data);
      setPagination(response.pagination);

      // Extract unique tags from customers
      const tags = Array.from(
        new Set(
          response.data.flatMap((customer) => customer.tags as TagsOutput[])
        )
      );
      setAvailableTags(tags);
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
      setPagination((prev) => ({
        ...prev,
        total: 0,
        pages: 0,
      }));
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  // Carregar dados iniciais apenas uma vez
  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Atualizar quando os filtros ou paginação mudarem
  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page, pagination.pageSize]);

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: newPageSize,
      page: 1, // Reset to first page when changing page size
    }));
  };

  const handleNavigate = (clientId?: string) => {
    if (clientId) {
      navigate(`/clientes/${clientId}/editar`);
    } else {
      navigate(`/clientes/novo`);
    }
  };

  const handleDelete = (id: string) => {
    openDeleteDialog(id);
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      try {
        setIsLoading(true);
        await service.delete(itemToDelete);

        // If we're on the last page and it's now empty, go to previous page
        if (customers.length === 1 && pagination.page > 1) {
          setPagination((prev) => ({ ...prev, page: prev.page - 1 }));
        } else {
          await fetchCustomers();
        }
      } finally {
        setIsLoading(false);
        closeDeleteDialog();
      }
    }
  };

  const addFilter = (type: FilterType, value: string | string[]) => {
    const existingFilterIndex = filters.findIndex((f) => f.type === type);

    if (existingFilterIndex >= 0) {
      const newFilters = [...filters];
      if (type === "tags") {
        const currentValues = newFilters[existingFilterIndex].value as string[];
        const newValue = value as string[];
        newFilters[existingFilterIndex].value = Array.from(
          new Set([...currentValues, ...newValue])
        );
      } else {
        newFilters[existingFilterIndex].value = value;
      }
      setFilters(newFilters);
    } else {
      setFilters((prev) => [
        ...prev,
        { type, value: type === "tags" ? [value as string] : value },
      ]);
    }

    // Reset to first page when adding a filter
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const removeTagValue = (
    filter: FilterItem,
    valueToRemove: string
  ): FilterItem => {
    if (filter.type !== "tags") {
      return filter;
    }

    const values = filter.value as string[];
    const filteredValues = values.filter((v) => v !== valueToRemove);

    return {
      ...filter,
      value: filteredValues,
    };
  };

  const removeEmptyTagsFilters = (filter: FilterItem): boolean => {
    return filter.type !== "tags" || (filter.value as string[]).length > 0;
  };

  const removeFilter = (type: FilterType, valueToRemove?: string) => {
    setFilters((prev) => {
      if (type === "tags" && valueToRemove) {
        return prev
          .map((filter) => removeTagValue(filter, valueToRemove))
          .filter(removeEmptyTagsFilters);
      } else {
        return prev.filter((f) => f.type !== type);
      }
    });

    // Reset to first page when removing a filter
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  const FilterButton = () => (
    <>
      <div className="hidden md:block">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="h-8 border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
            >
              <Filter className="mr-2 h-4 w-4" />
              Filtrar
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <FilterContent
              addFilter={addFilter}
              availableTags={availableTags}
            />
          </PopoverContent>
        </Popover>
      </div>
      <div className="md:hidden">
        <Sheet open={isFilterSheetOpen} onOpenChange={setIsFilterSheetOpen}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              className="h-8 border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
            >
              <Filter className="mr-2 h-4 w-4" />
              Filtrar
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[80vh]">
            <SheetHeader>
              <SheetTitle>Filtros</SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              <FilterContent
                addFilter={addFilter}
                availableTags={availableTags}
                onClose={() => setIsFilterSheetOpen(false)}
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );

  return (
    <div className="p-4 sm:p-6 w-full max-w-none">
      <Card className="overflow-hidden border-none shadow-md w-full">
        <CardHeader className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] text-white">
          <div className="flex items-center gap-3">
            <User className="h-6 w-6" />
            <CardTitle className="text-xl">Gerenciamento de Clientes</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <FilterButton />
              <div className="flex flex-wrap gap-2">
                {filters.map((filter) => {
                  if (filter.type === "tags") {
                    const tagValues = filter.value as string[];
                    return tagValues.map((tagId) => {
                      const tag = availableTags.find((t) => t.id === tagId);
                      return (
                        <Badge key={tagId} variant="secondary" className="h-8">
                          Tag: {tag?.name}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="ml-2 h-4 w-4 p-0"
                            onClick={() => removeFilter("tags", tagId)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      );
                    });
                  }
                  return (
                    <Badge
                      key={filter.type}
                      variant="secondary"
                      className="h-8"
                    >
                      {filter.type === "query"
                        ? `Nome: ${filter.value}`
                        : String(filter.value)}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-2 h-4 w-4 p-0"
                        onClick={() => removeFilter(filter.type)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  );
                })}
              </div>
            </div>

            <Button
              onClick={() => handleNavigate()}
              size="sm"
              className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white w-full md:w-auto"
            >
              <Plus className="h-4 w-4 mr-2" /> Cadastrar Cliente
            </Button>
          </div>

          {customers.length > 0 ? (
            <>
              <div className="hidden md:block border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader className="bg-gray-50">
                    <TableRow>
                      <TableHead>Nome do Cliente</TableHead>
                      <TableHead>DDD</TableHead>
                      <TableHead>Telefone</TableHead>
                      <TableHead>Tags</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customers.map((customer) => (
                      <TableRow key={customer.id} className="hover:bg-gray-50">
                        <TableCell className="font-medium">
                          {customer.name}
                        </TableCell>
                        <TableCell>{customer.ddd}</TableCell>
                        <TableCell>
                          {formatPhoneNumber(customer.phoneNumber)}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(customer.tags as TagsOutput[]).map((tag) => (
                              <Badge
                                key={tag.id}
                                style={{
                                  backgroundColor:
                                    tag.styling?.primaryColor ?? "#E0E0E0",
                                  color: tag.styling?.fontColor ?? "#000000",
                                  border: `1px solid ${
                                    tag.styling?.secondaryColor ?? "#BDBDBD"
                                  }`,
                                }}
                                className="rounded-sm px-1 py-0.5 text-xs"
                              >
                                {tag.name}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {formatWhatsappLink(
                              customer.ddd,
                              customer.phoneNumber,
                              customer.internationalCode
                            ) && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-green-500 text-green-500 hover:bg-green-50"
                                onClick={() =>
                                  window.open(
                                    formatWhatsappLink(
                                      customer.ddd,
                                      customer.phoneNumber,
                                      customer.internationalCode
                                    )!,
                                    "_blank"
                                  )
                                }
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="h-4 w-4 mr-1"
                                >
                                  <path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21" />
                                  <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1Z" />
                                  <path d="M13.5 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1Z" />
                                  <path d="M9 13.5a.5.5 0 0 0 .5.5h5a.5.5 0 0 0 0-1h-5a.5.5 0 0 0-.5.5Z" />
                                </svg>
                                WhatsApp
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
                              onClick={() => handleNavigate(customer.id)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Editar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-500 text-red-500 hover:bg-red-50"
                              onClick={() => handleDelete(customer.id)}
                            >
                              <Trash className="h-4 w-4 mr-1" />
                              Excluir
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="md:hidden divide-y border rounded-md">
                {customers.map((customer) => (
                  <CustomerCard
                    key={customer.id}
                    customer={customer}
                    onEdit={() => handleNavigate(customer.id)}
                    onDelete={() => handleDelete(customer.id)}
                  />
                ))}
              </div>

              {customers.length > 0 && (
                <PaginationControls
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                />
              )}
            </>
          ) : (
            <div className="text-center py-12 border rounded-lg bg-gray-50 w-full">
              <User className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                Nenhum cliente encontrado
              </h3>
              <p className="text-gray-500 mb-6">
                {filters.length > 0
                  ? "Não encontramos clientes com os filtros aplicados"
                  : "Você ainda não possui clientes cadastrados"}
              </p>
              <Button
                onClick={() => handleNavigate()}
                className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Cadastrar Cliente
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <DeleteConfirmationAlert
        isOpen={isOpen}
        onClose={closeDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir este cliente? Esta ação não pode ser desfeita."
        itemName={
          customers.find((c) => c.id === itemToDelete)?.name || "este cliente"
        }
      />
    </div>
  );
};

export default Customers;
